import type { ApiErrorBody } from "./types";

/**
 * Same-origin in development thanks to the Vite proxy, which is what lets the
 * HttpOnly `fdl_session` cookie work at all.
 */
export const API_BASE: string = import.meta.env.VITE_API_BASE_URL ?? "/api";

export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;
  readonly code: string | undefined;

  constructor(status: number, detail: string, code?: string) {
    super(detail);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
    this.code = code;
  }

  /** A 401 is a normal unauthenticated state, not a failure worth shouting about. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }
}

/** Network-level failure: no response at all. */
export function isOffline(error: unknown): boolean {
  return error instanceof TypeError;
}

export type QueryValue = string | number | boolean | null | undefined;

/** Build a path with a query string, dropping empty values. */
export function withQuery(path: string, params: Record<string, QueryValue> = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `${path}?${qs}` : path;
}

async function readError(response: Response): Promise<ApiError> {
  let detail = response.statusText || `Request failed with ${response.status}`;
  let code: string | undefined;
  try {
    const body: unknown = await response.json();
    if (body !== null && typeof body === "object") {
      const parsed = body as Partial<ApiErrorBody>;
      if (typeof parsed.detail === "string" && parsed.detail.length > 0) detail = parsed.detail;
      if (typeof parsed.code === "string") code = parsed.code;
    }
  } catch {
    // A non-JSON error body (a proxy error page, say). Keep the status text.
  }
  return new ApiError(response.status, detail, code);
}

type RequestOptions = {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
};

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, signal } = options;

  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    // Every request carries the session cookie.
    credentials: "include",
    body: body === undefined ? undefined : JSON.stringify(body),
    ...(signal ? { signal } : {}),
  });

  if (!response.ok) throw await readError(response);

  // 204 No Content, and any other empty body.
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  if (text.length === 0) return undefined as T;
  return JSON.parse(text) as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal): Promise<T> =>
    request<T>(path, signal ? { signal } : {}),

  post: <T>(path: string, body?: unknown): Promise<T> => request<T>(path, { method: "POST", body }),

  patch: <T>(path: string, body: unknown): Promise<T> =>
    request<T>(path, { method: "PATCH", body }),

  del: <T = void>(path: string): Promise<T> => request<T>(path, { method: "DELETE" }),
};

/**
 * Sign-in is an OAuth redirect chain, so it must be a real browser navigation.
 * Fetching it would follow the 302 to Google inside XHR and fail on CORS —
 * never call this with `fetch`.
 */
export function startGoogleSignIn(nextPath: string): void {
  const next = nextPath.startsWith("/") ? nextPath : "/";
  window.location.assign(`${API_BASE}/auth/google/start?next=${encodeURIComponent(next)}`);
}

/** Turn anything thrown into a sentence a person can act on. */
export function errorMessage(error: unknown, fallback = "Something went awry."): string {
  if (error instanceof ApiError) return error.detail;
  if (isOffline(error)) return "We couldn't reach Foundling. Check your connection and try again.";
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}
